import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { EnvPill } from '@/components/badges';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useStoreApps, type StoreApp } from '@/api/endpointsStores';
import { useSeesProviders } from '@/lib/useAudience';
import { BindingSubmit } from './ProfileBindingSubmit';
import { buildConfigCopy, type ProjectEnvironment } from './profileFormModel';
import type { ProfileFormApi } from './useProfileForm';

/** The error line under a binding (also the target of the input's aria-describedby). */
function BindingError({ env, error }: { env: ProjectEnvironment; error: string }) {
  return (
    <p id={`profile-binding-${env.slug}-error`} data-testid={`profile-binding-${env.slug}-error`} className="flex items-start gap-1 pl-1 text-xs text-destructive">
      <Icon name="error" size={14} className="mt-px" />
      <span>{error}</span>
    </p>
  );
}

/** The eas.json profile / build workflow input of one binding (no provider named, SPEC decision 23). */
function BindingInput({ env, form, error }: { env: ProjectEnvironment; form: ProfileFormApi; error: string | undefined }) {
  const b = form.values.bindings[env.id] ?? { on: false, profile: '' };
  const bitrise = form.bitriseProfile;
  const copy = buildConfigCopy(useSeesProviders());
  return (
    <Input
      data-testid={`profile-binding-${env.slug}-profile`}
      value={b.profile}
      disabled={!b.on}
      onChange={(e) => form.setBindingProfile(env.id, e.target.value)}
      placeholder={bitrise ? `Build workflow, e.g. ${form.values.platform === 'ios' ? 'ios-release' : 'android-apk'}` : copy.bindingPlaceholder(env.slug)}
      className="h-8 font-mono max-sm:h-10 sm:text-[13px]"
      maxLength={bitrise ? 100 : 64}
      spellCheck={false}
      aria-label={bitrise ? `Build workflow for ${env.name}` : copy.bindingLabel(env.name)}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `profile-binding-${env.slug}-error` : undefined}
    />
  );
}

/** One environment: a checkbox to build for it, its pill, the profile/workflow it builds with, and where it submits. */
function BindingRow({ env, form, storeApps }: { env: ProjectEnvironment; form: ProfileFormApi; storeApps: StoreApp[] }) {
  const b = form.values.bindings[env.id] ?? { on: false, profile: '' };
  const error = form.bindingError(env.id);
  return (
    <div className="flex flex-col gap-1">
      <div data-testid={`profile-binding-${env.slug}`} className={cn('grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1.2fr)] items-center gap-3 rounded-lg border px-3 py-2', b.on ? 'bg-background' : 'bg-secondary/40 opacity-80', error && 'border-destructive/50')}>
        <Checkbox data-testid={`profile-binding-${env.slug}-on`} checked={b.on} onCheckedChange={(v) => form.setBindingOn(env.id, v === true)} aria-label={`Build for ${env.name}`} />
        <EnvPill env={env} />
        <BindingInput env={env} form={form} error={error} />
      </div>
      {error ? <BindingError env={env} error={error} /> : null}
      <BindingSubmit env={env} form={form} storeApps={storeApps} />
    </div>
  );
}

/** Which environments the profile builds for, with which eas.json profile / build workflow, and where each submits. */
export function ProfileBindings({ form }: { form: ProfileFormApi }) {
  const copy = buildConfigCopy(useSeesProviders());
  const storeApps = useStoreApps(form.appId).data ?? [];
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1">
        <Kicker className="text-subtle">Environments</Kicker>
      </legend>
      {form.environments.map((env) => (
        <BindingRow key={env.id} env={env} form={form} storeApps={storeApps} />
      ))}
      <p className="text-xs text-subtle">{form.bitriseProfile ? 'Each environment builds with its build workflow (Hangar sets the workflows up).' : copy.emptyBinding}</p>
    </fieldset>
  );
}
